import { TokenResponse } from '../../domain/models/api';

/**
 * Token storage + JWT decode for the web login session (D9).
 *
 * Mirrors getspeakhub-portal's `core/services/token.service.ts` (same api-gw
 * OAuth2 authorization server, same token shape) — adapted to plain
 * React/localStorage since this app has no Angular DI or SSR platform check
 * to worry about (getspeakhub-ui is a client-side-only SPA; see server/app.js,
 * which only serves the static build, never renders it).
 *
 * Deliberately namespaced (community_*) and stored separately from anything
 * `/auth/login` might ever read — this is a second, independent session.
 */

const ACCESS_TOKEN_KEY = 'community_access_token';
const REFRESH_TOKEN_KEY = 'community_refresh_token';
const TOKEN_EXPIRY_KEY = 'community_token_expiry';

// How long before expiry a still-valid token is considered due for refresh.
const REFRESH_BUFFER_MS = 60 * 1000;

interface JwtPayload {
  id?: number;
  sub?: string;
  email?: string;
  roles?: string[];
  authorities?: string[];
  iat: number;
  exp: number;
}

type AuthChangeListener = () => void;
const listeners = new Set<AuthChangeListener>();

function notifyChange(): void {
  listeners.forEach((listener) => listener());
}

function decodeToken(token: string): JwtPayload | null {
  try {
    const base64Payload = token.split('.')[1];
    const base64 = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(decoded) as JwtPayload;
  } catch (error) {
    console.error('[TokenStorage] Failed to decode JWT:', error);
    return null;
  }
}

export const TokenStorage = {
  /** Notified on every save/clear — drives useAuth() and RequireAuth. */
  subscribe(listener: AuthChangeListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  saveTokens(tokenResponse: TokenResponse): void {
    const expiryTime = Date.now() + tokenResponse.expires_in * 1000;
    localStorage.setItem(ACCESS_TOKEN_KEY, tokenResponse.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokenResponse.refresh_token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiryTime));
    notifyChange();
  },

  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    notifyChange();
  },

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  getTokenExpiry(): number {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : 0;
  },

  isTokenValid(): boolean {
    const token = this.getAccessToken();
    const expiry = this.getTokenExpiry();
    return !!token && expiry > Date.now();
  },

  shouldRefreshToken(): boolean {
    if (!this.isTokenValid()) return false;
    return this.getTokenExpiry() - Date.now() < REFRESH_BUFFER_MS;
  },

  decodeToken,

  getUserId(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;
    return decodeToken(token)?.id ?? null;
  },

  getUserRoles(): string[] {
    const token = this.getAccessToken();
    if (!token) return [];
    const payload = decodeToken(token);
    return payload?.authorities ?? payload?.roles ?? [];
  },

  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  },
};
